import React from 'react'
import { Box, Grid2, Typography } from '@mui/material'
import {
  PISectionInput,
  PISectionValidation,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { GRID_CONFIG } from '../utils/patientFormConfig'

export const PatientFormGridItem = ({
  children,
  gridConfig = GRID_CONFIG,
  style,
  className,
  ...gridProps
}) => {
  return (
    <Grid2
      size={gridConfig}
      style={{ width: '100%', minWidth: 0, ...style }}
      className={className}
      {...gridProps}
    >
      {children}
    </Grid2>
  )
}

export const PatientFieldLabel = ({ children, required = false, style }) => {
  return (
    <Typography
      component="div"
      sx={{
        width: '100%',
        marginBottom: '6px',
        fontSize: '13px',
        lineHeight: 1.35,
        fontWeight: 600,
        color: 'rgba(15, 23, 42, 0.88)',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
      style={style}
    >
      {required && !String(children || '').includes('*') ? `${children}*` : children}
    </Typography>
  )
}

export const PatientFieldHelperText = ({ children, style }) => {
  if (!children) {
    return null
  }

  return (
    <div
      style={{
        color: '#64748b',
        fontSize: '12px',
        marginTop: '6px',
        lineHeight: 1.45,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export const FormField = ({
  label,
  value,
  onChange,
  onKeyDown,
  type = 'text',
  placeholder,
  disabled = false,
  required = false,
  min,
  max,
  children,
  validationError,
  helperText,
  showValidation = true,
  gridConfig = GRID_CONFIG,
  style,
  inputStyle,
  className,
  inputClassName,
  ...inputProps
}) => {
  const hasError = validationError && showValidation

  const renderInput = () => {
    if (children) {
      return children
    }

    return (
      <PISectionInput
        type={type}
        value={value || ''}
        onChange={onChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        min={min}
        max={max}
        style={{
          width: '100%',
          minHeight: '40px',
          boxSizing: 'border-box',
          fontSize: '14px',
          ...inputStyle,
        }}
        className={inputClassName}
        {...inputProps}
      />
    )
  }

  return (
    <PatientFormGridItem gridConfig={gridConfig} style={style} className={className}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
        {label && <PatientFieldLabel required={required}>{label}</PatientFieldLabel>}
        {renderInput()}
        <PatientFieldHelperText>{helperText}</PatientFieldHelperText>
        {hasError && <PISectionValidation>{validationError}</PISectionValidation>}
      </Box>
    </PatientFormGridItem>
  )
}

export const DisplayFormField = ({
  label,
  value,
  gridConfig = GRID_CONFIG,
  style,
  className,
}) => {
  return (
    <PatientFormGridItem gridConfig={gridConfig} style={style} className={className}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
        {label && <PatientFieldLabel>{label}</PatientFieldLabel>}
        <div>{value}</div>
      </Box>
    </PatientFormGridItem>
  )
}

export const CheckboxFormField = ({
  label,
  checked,
  onChange,
  disabled = false,
  children,
  gridConfig = GRID_CONFIG,
  style,
  className,
  checkboxProps = {},
}) => {
  return (
    <PatientFormGridItem gridConfig={gridConfig} style={style} className={className}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
        <Typography
          component="label"
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            width: '100%',
            marginBottom: children ? '6px' : 0,
            fontSize: '13px',
            lineHeight: 1.45,
            fontWeight: 500,
            color: 'rgba(15, 23, 42, 0.88)',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...checkboxProps}
          />
          {label}
        </Typography>
        {children}
      </Box>
    </PatientFormGridItem>
  )
}

export const ArrayFormField = ({
  label,
  items = [],
  onAdd,
  onRemove,
  onUpdate,
  renderItem,
  addButtonText = 'Add',
  disabled = false,
  gridConfig = GRID_CONFIG,
  style,
  className,
  AddIcon,
  helperText,
}) => {
  return (
    <PatientFormGridItem gridConfig={gridConfig} style={style} className={className}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            width: '100%',
            marginBottom: '6px',
            fontSize: '13px',
            lineHeight: 1.35,
            fontWeight: 600,
            color: 'rgba(15, 23, 42, 0.88)',
          }}
        >
          {label}
          {AddIcon && (
            <AddIcon
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
              onClick={() => {
                if (!disabled && onAdd) {
                  onAdd()
                }
              }}
            />
          )}
        </Typography>
        <PatientFieldHelperText style={{ marginTop: 0, marginBottom: '6px' }}>
          {helperText}
        </PatientFieldHelperText>
        {items.map((item, index) =>
          renderItem ? (
            renderItem(item, index, { onUpdate, onRemove, disabled })
          ) : (
            <div key={index} className="d-flex align-items-center gap-2 mb-2">
              <span>{item}</span>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => !disabled && onRemove(index)}
                  disabled={disabled}
                  style={{ cursor: 'pointer' }}
                >
                  Remove
                </button>
              )}
            </div>
          )
        )}
      </Box>
    </PatientFormGridItem>
  )
}

export default FormField
